import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ClipboardCheck, Loader2, Lock, History } from 'lucide-react';
import { haccpApi, erroreHaccp, oggiIso, giorniFa, dataIt } from '../haccpApi';
import { RisultatoIspezione } from './RisultatoIspezione';
import { GIUDIZI } from './VotoAnello';

const PASSI = ['Controllo le temperature dei frigoriferi', 'Controllo pulizie e sanificazione', 'Guardo preparazioni ed etichette',
  'Controllo DDT e fornitori', 'Controllo oli esausti e frittura', "L'ispettore scrive il commento"];

const Attesa = () => {
  const [passo, setPasso] = useState(0);
  useEffect(() => { const t = setInterval(() => setPasso((p) => Math.min(p + 1, PASSI.length - 1)), 1800); return () => clearInterval(t); }, []);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-2" data-testid="ispezione-in-corso">
      {PASSI.map((t, i) => (
        <p key={t} className={`text-sm flex items-center gap-2 ${i < passo ? 'text-emerald-700' : i === passo ? 'text-gray-900 font-semibold' : 'text-gray-400'}`}>
          {i === passo ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="w-4 text-center">{i < passo ? '✓' : '·'}</span>}{t}
        </p>
      ))}
    </div>
  );
};

const Storico = ({ voci, corrente, onApri }) => (
  <div className="space-y-2" data-testid="ispezione-storico">
    <h4 className="flex items-center gap-2 text-sm font-bold text-gray-900"><History className="h-4 w-4" />Ispezioni precedenti</h4>
    {voci.map((s) => (
      <button key={s.id} onClick={() => onApri(s.id)} data-testid={`ispezione-storico-${s.id}`}
        className={`w-full flex items-center justify-between text-left rounded-xl border p-3 bg-white ${s.id === corrente ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-200'}`}>
        <span className="text-sm text-gray-800">{dataIt(s.creata_il)} {String(s.creata_il).slice(11, 16)} · dal {dataIt(s.dal)} al {dataIt(s.al)}</span>
        <span className="text-sm font-bold" style={{ color: GIUDIZI[s.giudizio]?.colore }}>{s.voto}/100</span>
      </button>
    ))}
  </div>
);

const Periodo = ({ dal, al, setDal, setAl }) => (
  <div className="grid grid-cols-2 gap-2">
    <label className="text-xs text-gray-600">Dal
      <input type="date" value={dal} max={al} onChange={(e) => setDal(e.target.value)} data-testid="ispezione-dal"
        className="mt-1 w-full h-11 px-3 rounded-lg border border-gray-300 text-sm text-gray-900 bg-white" />
    </label>
    <label className="text-xs text-gray-600">Al
      <input type="date" value={al} min={dal} max={oggiIso()} onChange={(e) => setAl(e.target.value)} data-testid="ispezione-al"
        className="mt-1 w-full h-11 px-3 rounded-lg border border-gray-300 text-sm text-gray-900 bg-white" />
    </label>
  </div>
);

export const IspezioneSection = () => {
  const [responsabile, setResponsabile] = useState(null);
  const [dal, setDal] = useState(giorniFa(29));
  const [al, setAl] = useState(oggiIso());
  const [isp, setIsp] = useState(null);
  const [storico, setStorico] = useState([]);
  const [busy, setBusy] = useState(false);

  const apri = useCallback(async (id) => {
    try { setIsp((await haccpApi.get(`/ispezione/${id}`)).ispezione); } catch (e) { toast.error(erroreHaccp(e, 'Ispezione non trovata')); }
  }, []);

  const carica = useCallback(async () => {
    const { ispezioni } = await haccpApi.get('/ispezione/storico');
    setStorico(ispezioni);
    return ispezioni;
  }, []);

  useEffect(() => {
    haccpApi.get('/ispezione/permessi').then(async ({ responsabile: r }) => {
      setResponsabile(r);
      if (r) { const lista = await carica(); if (lista[0]) apri(lista[0].id); }
    }).catch(() => setResponsabile(false));
  }, [carica, apri]);

  const avvia = async () => {
    setBusy(true);
    try {
      const { ispezione } = await haccpApi.post('/ispezione/avvia', { dal, al });
      setIsp(ispezione);
      await carica();
    } catch (e) { toast.error(erroreHaccp(e, 'Ispezione non riuscita, riprova')); }
    finally { setBusy(false); }
  };

  if (responsabile === null) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!responsabile) {
    return (
      <div className="p-5"><div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-3" data-testid="ispezione-solo-responsabile">
        <Lock className="h-5 w-5 text-gray-500" /><p className="text-sm text-gray-700">L'ispezione simulata può farla solo il responsabile.</p>
      </div></div>
    );
  }
  return (
    <div className="p-4 lg:p-6 space-y-5" data-testid="ispezione-section">
      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gray-900 flex items-center justify-center shrink-0"><ClipboardCheck className="h-6 w-6 text-white" /></div>
          <div>
            <h3 className="font-bold text-gray-900">Ispezione ASL simulata</h3>
            <p className="text-sm text-gray-600">L'app controlla i tuoi registri come farebbe un ispettore e ti dice cosa sistemare prima della visita.</p>
          </div>
        </div>
        <Periodo dal={dal} al={al} setDal={setDal} setAl={setAl} />
        <button onClick={avvia} disabled={busy || !dal || !al} data-testid="ispezione-avvia"
          className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ClipboardCheck className="h-5 w-5" />}{busy ? 'Ispezione in corso...' : 'Avvia ispezione'}
        </button>
      </div>
      {busy && <Attesa />}
      {!busy && isp && <RisultatoIspezione isp={isp} />}
      {storico.length > 1 && <Storico voci={storico} corrente={isp?.id} onApri={apri} />}
    </div>
  );
};

export default IspezioneSection;
